import * as CryptoJS from 'crypto-js';

const PUBLIC_KEY = '98ebee6f9e6d51bac947baea3733e1108ef987e5a582798c7af4b5a3efabb631';
const PRIVATE_KEY = '8b753c889a72aba82f5f1712738c25ebf9e6b3bafeb848c7cbce96d0e8122c93';

// ─── Internal helpers ─────────────────────────────────────────────────────────

function wordArrayToBytes(wa: CryptoJS.lib.WordArray): Uint8Array {
  const words = wa.words;
  const sigBytes = wa.sigBytes;
  const out = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    out[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return out;
}

function bytesToWordArray(bytes: Uint8Array): CryptoJS.lib.WordArray {
  const words: number[] = [];
  for (let i = 0; i < bytes.length; i += 4) {
    words.push(
      ((bytes[i] || 0) << 24) |
      ((bytes[i + 1] || 0) << 16) |
      ((bytes[i + 2] || 0) << 8) |
      (bytes[i + 3] || 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, bytes.length);
}

function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

function deriveKey(): CryptoJS.lib.WordArray {
  return CryptoJS.SHA256(CryptoJS.enc.Utf8.parse(PUBLIC_KEY));
}

function computeHmac(ciphertext: Uint8Array, iv: Uint8Array): Uint8Array {
  const privateKeyWA = CryptoJS.enc.Utf8.parse(PRIVATE_KEY);
  const dataWA = bytesToWordArray(new Uint8Array([...ciphertext, ...iv]));
  const hmac = CryptoJS.HmacSHA256(dataWA, privateKeyWA);
  return wordArrayToBytes(hmac);
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function decryptField(value: string | null | undefined): string {
  if (!value) return '';

  try {
    // 1. Base64-decode
    const decoded = wordArrayToBytes(CryptoJS.enc.Base64.parse(value));
    if (decoded.length < 49) return value; // too short to be a valid blob, return original

    // 2. Split the blob
    const iv = decoded.slice(0, 16);
    const hash = decoded.slice(16, 48);
    const ciphertext = decoded.slice(48);

    // 3. Derive key
    const key = deriveKey();

    // 4. Verify HMAC
    const computedHash = computeHmac(ciphertext, iv);
    if (!timingSafeEqual(computedHash, hash)) {
      console.warn('[crypto] HMAC verification failed — value may not be encrypted or key mismatch');
      return value;
    }

    // 5. AES-256-CBC decrypt
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: bytesToWordArray(ciphertext) } as CryptoJS.lib.CipherParams,
      key,
      { iv: bytesToWordArray(iv), mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    const result = decrypted.toString(CryptoJS.enc.Utf8);
    return result || value;
  } catch (err) {
    console.error('[crypto] decryptField error:', err);
    return value || '';
  }
}

export function encryptField(value: string | null | undefined): string {
  if (!value) return '';

  try {
    const key = deriveKey();
    
    // 1. Generate IV (16 random bytes)
    const ivWA = CryptoJS.lib.WordArray.random(16);
    const iv = wordArrayToBytes(ivWA);

    // 2. AES-256-CBC encrypt
    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(value),
      key,
      { iv: ivWA, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    const ciphertext = wordArrayToBytes(encrypted.ciphertext);

    // 3. Compute HMAC hash of (ciphertext + iv)
    const hash = computeHmac(ciphertext, iv);

    // 4. Concatenate: iv (16 bytes) + hash (32 bytes) + ciphertext
    const blob = new Uint8Array(16 + 32 + ciphertext.length);
    blob.set(iv, 0);
    blob.set(hash, 16);
    blob.set(ciphertext, 48);

    // 5. Base64 encode
    const blobWA = bytesToWordArray(blob);
    return CryptoJS.enc.Base64.stringify(blobWA);
  } catch (err) {
    console.error('[crypto] encryptField error:', err);
    return value || '';
  }
}

